// components/ui/FormField.tsx
// UI Phase 1 primitive: label + control + description/error wiring.
// Renders children inside a labelled group and exposes the generated ids via
// a render prop so the control gets aria-describedby/aria-invalid for free.

import { useId } from 'react';
import type { ReactNode } from 'react';
import { cn } from './cn';

export interface FormFieldRenderProps {
  id: string;
  'aria-describedby': string | undefined;
  'aria-invalid': boolean | undefined;
}

export interface FormFieldProps {
  label: string;
  /** Secondary hint shown under the control */
  description?: string;
  /** Error message; presence switches the field into its error state */
  error?: string;
  required?: boolean;
  className?: string;
  children: (props: FormFieldRenderProps) => ReactNode;
}

const FormField = ({ label, description, error, required, className, children }: FormFieldProps) => {
  const id = useId();
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;

  // The description <p> is suppressed while an error shows, so its id must
  // leave aria-describedby too — no dangling references.
  const describedBy =
    [error ? errorId : null, description && !error ? descriptionId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && (
          <span className="text-status-critical ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {description && !error && (
        <p id={descriptionId} className="text-xs text-ink-2">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs font-medium text-status-critical">
          {error}
        </p>
      )}
    </div>
  );
};

export default FormField;
